import { FC, Suspense, useEffect, useRef, useState, useTransition } from 'react';
import { graphql, useLazyLoadQuery, useMutation, usePaginationFragment } from 'react-relay';
import { BasicButton, PrimaryButton } from 'src/components/widgets/buttons';
import { CatalogListContent } from 'src/components/widgets/catalogList';
import { CustomErrorBoundary } from 'src/components/widgets/customErrorBoundary';
import { QuestionDialog, useDialogManager } from 'src/components/widgets/dialog';
import { FiltersRow } from 'src/components/widgets/filters';
import { FormControl } from 'src/components/widgets/formControl';
import { Icon } from 'src/components/widgets/icon';
import Loader from 'src/components/widgets/loader';
import IfCanAccess from 'src/components/widgets/ifCanAccess/ifCanAccess';
import { showErrorNotification, showSuccessNotification } from 'src/components/widgets/notificationToaster';
import { Tab, TabList, TabPanel } from 'src/components/widgets/ztabs';
import { AdvancedSelect } from 'src/components/widgets/select';
import Stack from 'src/components/widgets/stack/stack';
import PresetConfigurationEditorDialog from 'src/components/pages/presetConfigurations/products/presetConfigurationEditorDialog';
import { deletePresetConfigurationCompositionMutation } from 'src/components/pages/presetConfigurations/products/presetConfigurationsQueries';
import { presetConfigurationsQueriesDeleteCompositionMutation } from 'src/components/pages/presetConfigurations/products/__generated__/presetConfigurationsQueriesDeleteCompositionMutation.graphql';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import { stringFormat, T } from 'src/shared/helpers';
import AddIcon from 'src/shared/material_icons/content/add.svg';
import EditIcon from 'src/shared/material_icons/image/edit.svg';
import RemoveIcon from 'src/shared/material_icons/content/remove.svg';
import PresetIcon from 'src/shared/material_icons/content/inventory_2.svg';
import DeleteIcon from 'src/shared/material_icons/action/delete.svg';
import RefreshIcon from 'src/shared/material_icons/action/autorenew.svg';
import {
    ComposerPanel,
    ComposerSettingsBody,
    ComposerToolbar,
    ComposerSubjectHeader,
    ComposerSubjectMark,
    ComposerSubjectContent,
    ComposerSubjectNameInput,
    ComposerSubjectMeta,
    ComposerKindBadge,
    ComposerTabs,
    ComposerFormSectionTitle,
    ComposerFormStack,
    ComposerFormSection,
    ComposerUnsupportedNote
} from '../../../configurator-components/composer-editors/composerWorkspace';
import { ComposerAttributeNode } from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { emptyPresetSelection, getPresetMode, getPresetOrigins, PresetMode, PresetSelection } from '../helpers/composerPresetSelection';
import useComposerStore from '../composerStore';
import { ComposerRevealButton } from '../../../configurator-components/composer-editors/composerReveal';
import { composerPresetConfigurationsQuery } from './__generated__/composerPresetConfigurationsQuery.graphql';
import { composerPresetConfigurationsPaginationQuery } from './__generated__/composerPresetConfigurationsPaginationQuery.graphql';
import { composerPresetConfigurations_list$key } from './__generated__/composerPresetConfigurations_list.graphql';

type Props = { attribute: ComposerAttributeNode; onPrepareEditor: (emptyPresetID?: string) => Promise<boolean> };

const PresetContent: FC<Props> = ({ attribute, onPrepareEditor }) => {
    const modelID = usePreview3DStore((store) => store.zkProduct?.get('modelID'))!;
    const updateAttribute = useComposerStore((store) => store.updateAttribute);
    const [search, setSearch] = useState('');
    const createdFrom = useRef<{ ids: Set<number>; closed: boolean } | null>(null);
    const [paginationError, setPaginationError] = useState<string | null>(null);
    const [fetchKey, setFetchKey] = useState(0);
    const [opening, setOpening] = useState(false);
    const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null);
    const [pending, startTransition] = useTransition();
    const { showDialog, closeDialog } = useDialogManager();
    const [deletePreset, deleting] = useMutation<presetConfigurationsQueriesDeleteCompositionMutation>(deletePresetConfigurationCompositionMutation);
    const query = useLazyLoadQuery<composerPresetConfigurationsQuery>(
        graphql`
            query composerPresetConfigurationsQuery($modelID: Long!, $count: Int = 60, $after: String) {
                compositionMacroCategories {
                    macroCategoryID
                    name
                    category {
                        categoryID
                        name
                    }
                }
                ...composerPresetConfigurations_list @arguments(modelID: $modelID, count: $count, after: $after)
            }
        `,
        { modelID },
        { fetchPolicy: 'network-only', fetchKey }
    );
    const { data, loadNext, hasNext, isLoadingNext } = usePaginationFragment<
        composerPresetConfigurationsPaginationQuery,
        composerPresetConfigurations_list$key
    >(
        graphql`
            fragment composerPresetConfigurations_list on Query
            @refetchable(queryName: "composerPresetConfigurationsPaginationQuery")
            @argumentDefinitions(modelID: { type: "Long!" }, count: { type: "Int", defaultValue: 60 }, after: { type: "String" }) {
                compositionsPaged(first: $count, after: $after, page: 1, modelID: $modelID, isTemplate: true, draft: false)
                    @connection(key: "composerPresetConfigurations_list_compositionsPaged") {
                    edges {
                        node {
                            ... on Composition {
                                id
                                compositionID
                                name
                                previewImageUrl
                                categories {
                                    categoryID
                                }
                            }
                        }
                    }
                }
            }
        `,
        query
    );
    const selection = attribute.presetSelection ?? emptyPresetSelection();
    const inferredMode = getPresetMode(selection);
    const mode = inferredMode ?? attribute.presetMode ?? null;
    const refresh = () =>
        startTransition(() => {
            setPaginationError(null);
            setFetchKey((key) => key + 1);
        });
    const setSelection = (presetSelection: PresetSelection) =>
        updateAttribute({ ...attribute, presetMode: mode && mode !== 'mixed' ? mode : undefined, presetSelection });
    const reset = () =>
        showDialog(
            'composer-reset-presets',
            <QuestionDialog
                destructive
                message={T._(
                    'Reset this pre-set configuration attribute? Its selected sources will be removed so you can choose another grouping mode.',
                    'Admin'
                )}
                onNoClick={() => closeDialog('composer-reset-presets')}
                onYesClick={() => {
                    updateAttribute({ ...attribute, presetMode: undefined, presetSelection: emptyPresetSelection() });
                    closeDialog('composer-reset-presets');
                }}
            />
        );
    const openEditor = async (templateID?: number) => {
        if (opening) return;
        setOpening(true);
        try {
            if (!(await onPrepareEditor(!inferredMode && templateID == null ? attribute.id : undefined))) return;
            if (templateID == null) createdFrom.current = { ids: new Set(nodes.map((node) => node.compositionID)), closed: false };
            showDialog(
                'composer-preset-editor',
                <PresetConfigurationEditorDialog
                    modelID={modelID}
                    templateID={templateID}
                    onClose={() => {
                        closeDialog('composer-preset-editor');
                        if (createdFrom.current) createdFrom.current.closed = true;
                        refresh();
                    }}
                />
            );
        } catch (error) {
            showErrorNotification({ message: error instanceof Error ? error.message : T._('Could not open the pre-set configuration editor.', 'Admin') });
        } finally {
            setOpening(false);
        }
    };
    const removePreset = (id: string, compositionID: number) =>
        showDialog(
            'composer-delete-preset',
            <QuestionDialog
                destructive
                message={T._(
                    'Delete this pre-set configuration from the product? It will also be removed from other attributes that select it individually.',
                    'Admin'
                )}
                onNoClick={() => closeDialog('composer-delete-preset')}
                onYesClick={() => {
                    closeDialog('composer-delete-preset');
                    deletePreset({
                        variables: { deleteCompositionId: id },
                        onCompleted: (_response, errors) => {
                            if (errors?.length) {
                                showErrorNotification({ message: errors.map((error) => error.message).join(', ') });
                                return;
                            }
                            // A deleted preset can be referenced by more than one container in this draft.
                            const store = useComposerStore.getState();
                            store.attributes.forEach((item) => {
                                if (item.presetSelection?.compositions.includes(compositionID))
                                    store.updateAttribute({
                                        ...item,
                                        presetSelection: {
                                            ...item.presetSelection,
                                            compositions: item.presetSelection.compositions.filter((value) => value !== compositionID)
                                        }
                                    });
                            });
                            showSuccessNotification({ message: T._('Pre-set configuration deleted successfully.', 'Admin') });
                            refresh();
                        },
                        onError: (error) => {
                            showErrorNotification({ message: error.message });
                        }
                    });
                }}
            />
        );
    const macros = query.compositionMacroCategories;
    const categoryOptions = macros.flatMap((macro) =>
        (macro.category ?? []).flatMap((category) =>
            category
                ? [
                      {
                          value: `${macro.macroCategoryID}:${category.categoryID}`,
                          label: `${macro.name} / ${category.name}`,
                          macroCategoryID: macro.macroCategoryID,
                          categoryID: category.categoryID
                      }
                  ]
                : []
        )
    );
    const nodes = (data.compositionsPaged?.edges ?? []).flatMap((edge) => (edge.node?.compositionID != null ? [edge.node] : []));
    useEffect(() => {
        if (hasNext && !isLoadingNext && !paginationError)
            loadNext(60, {
                onComplete: (error) => {
                    if (error) setPaginationError(error.message);
                }
            });
    }, [hasNext, isLoadingNext, loadNext, paginationError, nodes.length]);
    useEffect(() => {
        if (!createdFrom.current?.closed || hasNext || isLoadingNext || pending) return;
        const created = nodes.filter((node) => !createdFrom.current!.ids.has(node.compositionID)).map((node) => node.compositionID);
        // A newly created preset is included explicitly; cancelling the editor keeps the empty draft.
        if (created.length) {
            const current = useComposerStore.getState().attributes.find((item) => item.id === attribute.id);
            if (current && (getPresetMode(current.presetSelection) ?? current.presetMode) === 'compositions') {
                updateAttribute({
                    ...current,
                    presetSelection: { ...emptyPresetSelection(), compositions: [...new Set([...(current.presetSelection?.compositions ?? []), ...created])] }
                });
            }
        }
        createdFrom.current = null;
    }, [nodes, hasNext, isLoadingNext, pending, attribute.id, updateAttribute]);
    const macroOptions = macros.map((macro) => ({ value: macro.macroCategoryID, label: macro.name ?? '' }));
    const compositionOptions = nodes.map((node) => ({ value: node.compositionID, label: node.name ?? String(node.compositionID) }));
    const modeOptions: { value: PresetMode; label: string }[] = [
        { value: 'macrocategories', label: T._('By macro-categories', 'Admin') },
        { value: 'categories', label: T._('By subcategories', 'Admin') },
        { value: 'compositions', label: T._('By individual compositions', 'Admin') }
    ];
    const available = nodes.flatMap((node) => {
        const origins = getPresetOrigins(selection, node, macros);
        return origins.length ? [{ ...node, origins }] : [];
    });
    const originLabel = (origin: ReturnType<typeof getPresetOrigins>[number]) =>
        origin.mode === 'compositions'
            ? T._('Added individually', 'Admin')
            : `${origin.mode === 'macrocategories' ? T._('Included from macro-category', 'Admin') : T._('Included from subcategory', 'Admin')}: ${origin.name}`;
    const missingLabel = (id: number) => `${T._('Unavailable source', 'Admin')} (${id})`;
    const busy = opening || deleting || pending;
    return (
        <>
            <ComposerSubjectHeader>
                <ComposerSubjectMark $kind='attribute'>
                    <PresetIcon />
                </ComposerSubjectMark>
                <ComposerSubjectContent>
                    <ComposerSubjectNameInput
                        aria-label={T._('Pre-set configurations name', 'Admin')}
                        value={attribute.name}
                        onChange={(event) => updateAttribute({ ...attribute, name: event.currentTarget.value })}
                    />
                    <ComposerSubjectMeta>
                        <ComposerKindBadge $kind='attribute'>{T._('Pre-set configurations', 'Admin')}</ComposerKindBadge>
                        <span>
                            {stringFormat(available.length === 1 ? T._('{0} option', 'Admin') : T._('{0} options', 'Admin'), available.length.toString())}
                        </span>
                    </ComposerSubjectMeta>
                </ComposerSubjectContent>
                <ComposerRevealButton id={attribute.id} />
            </ComposerSubjectHeader>
            <ComposerTabs selectedIndex={0}>
                <TabList>
                    <Tab>{T._('Settings', 'Admin')}</Tab>
                </TabList>
                <TabPanel>
                    <ComposerSettingsBody ref={setScrollContainer}>
                        <ComposerFormStack>
                            <ComposerFormSection>
                                <FormControl label={T._('Group compositions by', 'Admin')}>
                                    <AdvancedSelect
                                        aria-label={T._('Group compositions by', 'Admin')}
                                        isDisabled={busy || !!inferredMode}
                                        placeholder={mode === 'mixed' ? T._('Mixed sources — reset required', 'Admin') : T._('Choose a grouping mode', 'Admin')}
                                        options={modeOptions}
                                        value={modeOptions.find((item) => item.value === mode) ?? null}
                                        onChange={(item: { value: PresetMode } | null) => {
                                            if (!inferredMode && item) updateAttribute({ ...attribute, presetMode: item.value });
                                        }}
                                    />
                                </FormControl>
                                {inferredMode && (
                                    <BasicButton type='button' $size='small' disabled={busy} onClick={reset}>
                                        {T._('Reset configuration', 'Admin')}
                                    </BasicButton>
                                )}
                                {mode === 'mixed' && (
                                    <p role='alert'>
                                        {T._(
                                            'This attribute contains different source types. Reset it and choose one mode, or create separate pre-set configuration attributes.',
                                            'Admin'
                                        )}
                                    </p>
                                )}
                                {mode === 'macrocategories' && (
                                    <FormControl label={T._('Include macro-categories', 'Admin')}>
                                        <AdvancedSelect
                                            aria-label={T._('Include macro-categories', 'Admin')}
                                            isMulti
                                            isDisabled={busy}
                                            options={macroOptions}
                                            value={selection.macrocategories.map(
                                                (id) => macroOptions.find((item) => item.value === id) ?? { value: id, label: missingLabel(id) }
                                            )}
                                            onChange={(items: { value: number }[]) =>
                                                setSelection({ ...emptyPresetSelection(), macrocategories: items.map((item) => item.value) })
                                            }
                                        />
                                    </FormControl>
                                )}
                                {mode === 'categories' && (
                                    <FormControl label={T._('Include subcategories', 'Admin')}>
                                        <AdvancedSelect
                                            aria-label={T._('Include subcategories', 'Admin')}
                                            isMulti
                                            isDisabled={busy}
                                            options={categoryOptions}
                                            value={selection.categories.map(
                                                (source) =>
                                                    categoryOptions.find(
                                                        (item) => item.categoryID === source.categoryID && item.macroCategoryID === source.macroCategoryID
                                                    ) ?? {
                                                        ...source,
                                                        value: `${source.macroCategoryID}:${source.categoryID}`,
                                                        label: missingLabel(source.categoryID)
                                                    }
                                            )}
                                            onChange={(items: typeof categoryOptions) =>
                                                setSelection({
                                                    ...emptyPresetSelection(),
                                                    categories: items.map(({ categoryID, macroCategoryID }) => ({ categoryID, macroCategoryID }))
                                                })
                                            }
                                        />
                                    </FormControl>
                                )}
                                {mode === 'compositions' && (
                                    <FormControl label={T._('Include individual compositions', 'Admin')}>
                                        <AdvancedSelect
                                            aria-label={T._('Include individual compositions', 'Admin')}
                                            isMulti
                                            isDisabled={busy}
                                            options={compositionOptions}
                                            value={selection.compositions.map(
                                                (id) => compositionOptions.find((item) => item.value === id) ?? { value: id, label: missingLabel(id) }
                                            )}
                                            onChange={(items: { value: number }[]) =>
                                                setSelection({ ...emptyPresetSelection(), compositions: items.map((item) => item.value) })
                                            }
                                        />
                                    </FormControl>
                                )}
                                <ComposerUnsupportedNote>
                                    {T._(
                                        'Choose a mode and at least one source before saving. Sources in this mode use OR. Reset the configuration to change mode.',
                                        'Admin'
                                    )}
                                    {(mode === 'macrocategories' || mode === 'categories') && (
                                        <>
                                            <br />
                                            {T._(
                                                'New compositions in the selected categories are included automatically. Inherited options are read-only.',
                                                'Admin'
                                            )}
                                        </>
                                    )}
                                    {mode === 'compositions' && (
                                        <>
                                            <br />
                                            {T._(
                                                'Only individually selected compositions are included. New compositions are not included automatically.',
                                                'Admin'
                                            )}
                                        </>
                                    )}
                                </ComposerUnsupportedNote>
                            </ComposerFormSection>
                            <ComposerToolbar>
                                {mode === 'compositions' && (
                                    <PrimaryButton
                                        type='button'
                                        $size='small'
                                        disabled={busy || hasNext || isLoadingNext || !!paginationError}
                                        onClick={() => void openEditor()}
                                    >
                                        <Icon>
                                            <AddIcon />
                                        </Icon>
                                        {T._('Create pre-set configuration', 'Admin')}
                                    </PrimaryButton>
                                )}
                                <BasicButton type='button' $size='small' disabled={busy} onClick={refresh}>
                                    <Icon>
                                        <RefreshIcon />
                                    </Icon>
                                    {T._('Refresh', 'Admin')}
                                </BasicButton>
                            </ComposerToolbar>
                            <ComposerFormSectionTitle>{T._('Options', 'Admin')}</ComposerFormSectionTitle>
                            {(hasNext || isLoadingNext) && <p role='status'>{T._('Loading all available compositions…', 'Admin')}</p>}
                            {paginationError && (
                                <p role='alert'>
                                    {paginationError} <BasicButton onClick={() => setPaginationError(null)}>{T._('Try again', 'Admin')}</BasicButton>
                                </p>
                            )}
                            <FiltersRow showSearch value={search} onSearchChange={setSearch} placeholder={T._('Search included compositions', 'Admin')} />
                            <CatalogListContent
                                models={available
                                    .filter((node) => (node.name ?? '').toLocaleLowerCase().includes(search.toLocaleLowerCase()))
                                    .map((node) => ({
                                        id: node.id,
                                        title: node.name ?? '',
                                        subtitle: node.origins.map(originLabel).join('; '),
                                        thumbnail: node.previewImageUrl,
                                        selected: false,
                                        readOnly: true,
                                        customFooter:
                                            mode !== 'compositions' ? (
                                                <div>
                                                    <strong>{node.name}</strong>
                                                    {node.origins.map((origin, index) => (
                                                        <div key={index}>
                                                            <small>{originLabel(origin)}</small>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : undefined,
                                        buttons:
                                            mode === 'compositions'
                                                ? [
                                                      { key: 'edit', tooltip: T._('Edit pre-set configuration', 'Admin'), icon: <EditIcon />, isActive: !busy },
                                                      {
                                                          key: 'remove',
                                                          tooltip: T._('Remove from this attribute', 'Admin'),
                                                          icon: <RemoveIcon />,
                                                          isActive: !busy
                                                      },
                                                      {
                                                          key: 'delete',
                                                          tooltip: T._('Delete pre-set configuration from product', 'Admin'),
                                                          icon: <DeleteIcon />,
                                                          isActive: !busy
                                                      }
                                                  ]
                                                : undefined
                                    }))}
                                loadNext={() => {}}
                                hasNext={false}
                                isLoadingNext={false}
                                scrollContainer={scrollContainer}
                                columns={{ xs: 1, md: 2, lg: 2 }}
                                noItemsMessage={T._('No included pre-set configurations.', 'Admin')}
                                noItemsHideIcon
                                onItemClick={() => {}}
                                onCatalogListItemButtonClick={(button, item) => {
                                    if (busy || mode !== 'compositions') return;
                                    const node = nodes.find((node) => node.id === item.id);
                                    if (!node) return;
                                    if (button.key === 'edit') void openEditor(node.compositionID);
                                    else if (button.key === 'remove')
                                        setSelection({ ...selection, compositions: selection.compositions.filter((id) => id !== node.compositionID) });
                                    else removePreset(node.id, node.compositionID);
                                }}
                            />
                        </ComposerFormStack>
                    </ComposerSettingsBody>
                </TabPanel>
            </ComposerTabs>
        </>
    );
};

const ComposerPresetConfigurations: FC<Props> = (props) => {
    const [retry, setRetry] = useState(0);
    return (
        <ComposerPanel aria-label={T._('Pre-set configurations', 'Admin')}>
            <IfCanAccess permission='PRESET_CONFIGURATIONS' layout='section'>
                <CustomErrorBoundary
                    resetKey={retry}
                    render={(error) => (
                        <Stack>
                            <p role='alert'>{error?.message}</p>
                            <BasicButton onClick={() => setRetry((key) => key + 1)}>{T._('Try again', 'Admin')}</BasicButton>
                        </Stack>
                    )}
                >
                    <Suspense fallback={<Loader />}>
                        <PresetContent key={retry} {...props} />
                    </Suspense>
                </CustomErrorBoundary>
            </IfCanAccess>
        </ComposerPanel>
    );
};
export default ComposerPresetConfigurations;
